import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { MdOutlinePerson, MdOutlinePersonOff } from 'react-icons/md';
import useOutsideClickHandler from 'hooks/useOutsideClickHandler';
import Spinner from 'assets/gif/loading.gif';
import axios from 'config/axios';
import Header from 'components/Header';
import Button from 'components/Button';
import Table from 'components/Table';
import Loader from 'components/Loader';
import QuestionPopup from 'components/QuestionPopup';

import './index.css';

function ClassBCAReport () {
    const [testData, setTestData] = useState(null);
    const [isQuestionPopupOpen, setIsQuestionPopupOpen] = useState(false);
    const [currentQuestionData, setCurrentQuestionData] = useState(null);
    const [correctStudent, setCorrectStudent] = useState(null);
    const [downloadInProgress, setDownloadInProgress] = useState(false);
    const [hideStudentNames, setHideStudentNames] = useState(false);

    const { classId, testId } = useParams();
    const questionPopupRef = useOutsideClickHandler(() => handleExit());
    const navigate = useNavigate();

    useEffect(() => {
        axios.get(`tutors/classes/${classId}/digital-bca/report/${testId}/`)
            .then(res => {
                setTestData(res.data);
            })
            .catch(error => {
                console.log(error);
            });
    }, [classId, testId]);

    const exportStudentTestData = () => {
        setDownloadInProgress(true);
        axios({
            url: `tutors/report/download/${classId}/${testId}/`,
            method: 'GET',
            responseType: 'blob',
        })
        .then(response => {
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${testData?.className}-${testData?.testName}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setDownloadInProgress(false);
        })
        .catch(error => {
            console.error('Error downloading the class report:', error);
            setDownloadInProgress(false);
        });
    }

    const scoredStudents = testData ? testData.students.filter(student => student.score !== null) : [];

    const handleQuestionPopup = (questionIndex: number) => {
        const question = testData.questions[questionIndex];
        const numCorrect = scoredStudents.filter(student => student.answers[questionIndex]?.status === 'correct').length;
        setCorrectStudent(scoredStudents.length > 0 ? 100 * numCorrect / scoredStudents.length : null);

        axios.get(`tutors/classes/report/question/${question.questionId}/${testId}/`)
            .then(res => {
                setCurrentQuestionData(res.data);
            })
            .catch(error => {
                console.log(error);
            });

        setIsQuestionPopupOpen(true);
    }

    const handleExit = () => {
        setIsQuestionPopupOpen(false);
        setCurrentQuestionData(null);
    }

    if (testData === null) return <Loader />;

    return (
        <div className='bcaClassReport'>
            <Helmet>
                <title>
                    Class Report - Test Center Admin
                </title>
            </Helmet>
            {isQuestionPopupOpen &&
                <QuestionPopup
                    info={currentQuestionData?.info}
                    correctStudent={correctStudent !== null ? `${correctStudent.toFixed(1)}%` : '-'}
                    testName={testData.testName}
                    question={currentQuestionData?.question}
                    passages={currentQuestionData?.passages}
                    figures={currentQuestionData?.figures}
                    choices={currentQuestionData?.choices}
                    correctChoice={currentQuestionData?.correctChoice}
                    correctAnswers={currentQuestionData?.correctAnswers}
                    manageExit={handleExit}
                    innerRef={questionPopupRef}
                />
            }
            <Header
                title={testData.testName}
                prevRoutes={[{ name: testData.className, path: `/classes/${classId}` }]}
            />
            <div className='bcaClassReport__subHeaderRow'>
                <h2 className='bcaClassReport__subHeader'>
                    Scores by students
                </h2>
                <div className='bcaClassReport__buttonGroup'>
                    <Button className='bcaClassReport__button' secondary onClick={() => setHideStudentNames(!hideStudentNames)}>
                        {hideStudentNames
                        ?   <>
                                <MdOutlinePerson className='bcaClassReport__buttonIcon' />
                                <p>Show</p>
                            </>
                        :   <>
                                <MdOutlinePersonOff className='bcaClassReport__buttonIcon' />
                                <p>Hide</p>
                            </>
                        }
                    </Button>
                </div>
            </div>
            <div className='bcaClassReport__scoresContainer'>
                <Table
                    className='bcaClassReport__table'
                    header={[
                        'Students',
                        <p className='bcaClassReport__cellCenter'>Score</p>,
                        <p className='bcaClassReport__cellCenter'>Correct</p>,
                        <p className='bcaClassReport__cellCenter'>Incorrect</p>,
                        <p className='bcaClassReport__cellCenter'>Omitted</p>,
                        <p className='bcaClassReport__cellCenter'>Status</p>
                    ]}
                    columnTypes={['string', 'number', 'number', 'number', 'number', null]}
                    rows={testData.students.map(student => [
                        hideStudentNames ? null : `${student.firstName} ${student.lastName}`,
                        // +2 / -0.5 per question: keep the half point and the sign.
                        student.score !== null ? Number(student.score) : null,
                        student.correct,
                        student.wrong,
                        student.blank,
                        <p className='bcaClassReport__cellCenter'>
                            {student.score !== null ? 'Completed' : student.inProgress ? 'In progress' : 'Not started'}
                        </p>
                    ])}
                    onSelectCell={testData.students.map(student => ([
                        hideStudentNames ? null : () => navigate(`/students/${student.studentId}`),
                        student.score !== null ? () => navigate(`/report/digital-bca/${student.testId}`) : null,
                        null,
                        null,
                        null,
                        null
                    ]))}
                    navigator={false}
                />
                <div className='bcaClassReport__scoreDistribution'>
                    <div className='bcaClassReport__scoreDistribution__content'>
                        <div className='bcaClassReport__scoreDistribution__avgScore'>
                            <div className='bcaClassReport__scoreDistribution__avg'>Avg Score</div>
                            <div className='bcaClassReport__scoreDistribution__avgScoreNum'>
                                {testData.avgScore !== null ? Number(testData.avgScore) : '-'}
                            </div>
                            <div className='bcaClassReport__scoreDistribution__avg'>
                                {testData.numScored} of {testData.students.length} completed
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className='bcaClassReport__subHeaderRow'>
                <h2 className='bcaClassReport__subHeader'>
                    Student responses
                </h2>
                <div className='bcaClassReport__buttonGroup'>
                    <Button className='bcaClassReport__button' onClick={exportStudentTestData}>
                        {downloadInProgress ? <img src={Spinner} /> : 'Download'}
                    </Button>
                </div>
            </div>
            <Table
                header={['Students', ...testData.questions.map((question, q) => (
                    <p
                        className='bcaClassReport__cellCenter bcaClassReport__response bcaClassReport__cursor bcaClassReport__tableHeader'
                        onClick={() => handleQuestionPopup(q)}
                    >
                        Q{question.questionNumber}
                    </p>
                ))]}
                rows={[
                    [
                        <p className='bcaClassReport__header'>Answer</p>,
                        ...testData.questions.map(question => (
                            <p className='bcaClassReport__header bcaClassReport__cellCenter'>{question.answer}</p>
                        ))
                    ],
                    [
                        <p className='bcaClassReport__header'># Incorrect</p>,
                        ...testData.questions.map((_question, q) => (
                            <p className='bcaClassReport__header bcaClassReport__cellCenter'>
                                {scoredStudents.filter(student => student.answers[q]?.status === 'wrong').length}
                            </p>
                        ))
                    ],
                    ...scoredStudents.map(student => ([
                        hideStudentNames ? null : `${student.firstName} ${student.lastName}`,
                        ...student.answers.map(answer => (
                            <p className={`bcaClassReport__cellCenter bcaClassReport__response ${
                                answer.status === 'wrong'
                                    ? 'bcaClassReport__cellWrong'
                                    : answer.status === 'blank'
                                    ? 'bcaClassReport__cellSkipped'
                                    : ''
                            }`}>
                                {answer.answer ? answer.answer : '-'}
                            </p>
                        ))
                    ])),
                ]}
                navigator={false}
            />
        </div>
    );
}

export default ClassBCAReport;
